import ProductSelection from "../../../domains2/orders/ProductSelection";
import appConfig from "../../../appconfig";
import { store } from "../../../store";
import {evaluateCustomerType, getExceededPriceRules} from "../../utils";
import { convertProductSelectionsToMap } from "../../../domains2/orders/OrderLineMappingHelper";
import { collectSelectionsWithDiscountOptions } from "../../../domains2/libraries/Utils";
import * as actions from "../../../store/mutation-types";
import AppUsersProvider from "../../../providers/AppUsersProvider";

export const getProductPriceWithDiscounts = (
	products,
	productSelections,
	productCatalog,
	discountTypeDisable = ["familyDiscount"]
) => {
	// Early return for empty products
	if (!products || !products.length) return {};

	const storeGetters = store.getters;
	const featureToggles = storeGetters.featureToggles || {};
	const tenantData = storeGetters.tenantSpecificData;
	const userData = storeGetters.applicationUserData;

	const orderLinesContext = {
		channel: appConfig.channelAlias(),
		deliveryMethod: tenantData.deliveryChoice,
		customerType: evaluateCustomerType(),
		dailySpending: featureToggles.discountDailyLimit && userData && userData.meta ? userData.meta.dailySpending : undefined,
		enableProductBestPriceInCatalogAndCart: featureToggles.enableProductBestPriceInCatalogAndCart,
		cartProductsMap: convertProductSelectionsToMap(storeGetters.cartProducts),
		exceededPriceRules: getExceededPriceRules()
	};

	const orderLines = ProductSelection.mapToOrderLinesGen(5)(
		productCatalog,
		productSelections,
		false,
		orderLinesContext,
		[],
		true,
		discountTypeDisable
	);

	// Relay on maps instead of arrays (and nested loops) for faster lookups
	const orderLinesMap = new Map();
	for (let i = 0; i < orderLines.length; i++) {
		const orderLine = orderLines[i];
		orderLinesMap.set(orderLine.productId, orderLine);
	}

	const result = {};

	for (let i = 0; i < products.length; i++) {
		const product = products[i];
		const productId = product.id;
		// if (product.price) {
		// 	// Handle products with direct price
		// 	const discountValue = shouldShowDiscountPrice(product, enableInGroupDiscount)
		// 		? getProductSpecificDiscount({ price: product.price, discount: product.discount })
		// 		: 0;
		// 	const basePrice = product.price;
		// 	const discountPrice = discountValue > basePrice ? basePrice : basePrice - discountValue;
		// 	result[productId] = { basePrice, discountPrice };
		// } else {
		const orderLine = orderLinesMap.get(productId);
		if (orderLine) {
			result[productId] = {
				basePrice: orderLine.totalPriceBeforeAccum || orderLine.totalPrice,
				discountPrice: orderLine.totalPriceAccum
			};
		}
	}
	return result;
};

export const UPSELL_FLOWS = Object.freeze({
	RCM_MARKETING_CARD: "rcmMarketingCard"
});

export const AUTH_FLOW_METHODS = Object.freeze({
	NORMAL: "normal",
	HISTORY: "history",
	CART: "cart",
	POPUP: "popup",
	QR: "QR",
	QR_NEW: "QR_NEW",
	AUTH_CODE: "auth_code"
});

export const AUTH_EVENT = Object.freeze({
	SIGN_UP: "sign_up",
	LOGIN: "login"
});

export const SUNSET_PLUS_UPSELL_MODAL_MINIMAL_SPENDING = 165;

export const getGlobalTemplateReplaceCssName = (tenantId, replaceCss) => {
	for (const key in replaceCss) {
		if (replaceCss[key].includes(tenantId)) {
			return key;
		}
	}
	return null;
};

export const generateRandomNumber = (max) => {
	return Math.floor(Math.random() * max);
};

export const extractShopContext = () => {
	const shop = store.getters.globalSelectedShop;
	const options = shop && shop.shopOptions;
	const stationData = options && options.dmiStationData;
	const stationId = stationData && stationData.id;

	const weatherData = options && options.shopWeatherData;
	const temperature = weatherData && weatherData.temperature;

	const shouldPushWeather =
	store.getters.featureToggles.enableWeatherSyncJob &&
	stationId &&
	weatherData &&
	temperature;

	return {
		shop,
		options,
		stationData,
		stationId,
		weatherData,
		temperature,
		shouldPushWeather
	};
};

export const isCurrentSelectionAccessible = (productSelections, self ) => {
	const currentCampaignSelections = (productSelections.map(selection => collectSelectionsWithDiscountOptions(selection)))
		.flat()
		.filter(selection => selection.discountOptions.label === "GiveawayCampaign");
	if(currentCampaignSelections.length > 0 ) {
		const existingCampaignSelections = (store.getters.selections.map(selection => collectSelectionsWithDiscountOptions(selection))).flat();

		// check whether there is a match on campaignLabel + productId
		const matchingCampaignSelectionsInBasket = existingCampaignSelections.filter(cur =>
			currentCampaignSelections.some(ex => cur.discountOptions.label === ex.discountOptions.label && cur.productId === ex.productId)
		);

		const usedCampaignsFromVuex = store.getters.usedCampaigns;
		// here we collect info about previous orders that contained promo products
		const matchingCampaignSelectionsFromPast = usedCampaignsFromVuex ? usedCampaignsFromVuex.filter(usedCampaign =>
			currentCampaignSelections.some(cur => usedCampaign.campaignId === cur.discountOptions.label && usedCampaign.productId === cur.productId)
		) : [];
		const matchingCampaignSelectionsFromPastUsageCount = matchingCampaignSelectionsFromPast.length ? matchingCampaignSelectionsFromPast[0].usageCounter : 0;
		// this is defined on price rules level
		const limitPerUser = currentCampaignSelections[0].discountOptions.quantityPerUserLimit;
		const quantity = currentCampaignSelections[0].quantity || 1;
		const isLimitReached = (matchingCampaignSelectionsInBasket.length + matchingCampaignSelectionsFromPastUsageCount + quantity) > limitPerUser;
		if(isLimitReached) {
			store.dispatch(actions.ADD_GLOBAL_ERROR, {friendlyMessage: self.$t("libraries.utils.promoProductLimitReached")});
			return false;
		}
	}
	return true;
};
export const getUsedCampaigns = async () => {
	try {
		const userData = store.getters.applicationUserData;
		const idpToken = store.getters.applicationUserIdpToken;
		const isSunsetPointsMember = store.getters.isSunsetPointsMember;

		if (!userData || !userData.id || !idpToken || !isSunsetPointsMember) {
			return [];
		}
		const appUser = await AppUsersProvider.find(userData.id, idpToken);
		return (appUser.data.meta && appUser.data.meta.usedCampaigns) ? appUser.data.meta.usedCampaigns : [];
	} catch (e) {
		return [];
	}
};
